import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";
import { Box, Breadcrumbs, Button, Typography, useMediaQuery } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import AllProductContext from "../../../context/allProducts/AllProductsContext";
import AllPropertiesContext from "../../../context/allProperties/AllPropertiesContext";
import AllReviewsContext from "../../../context/allReviews/AllReviewsContext";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import PropertiesAPI from "../../../api/firebase/PropertiesAPI";
import ReviewsAPI from "../../../api/firebase/ReviewsAPI";
import ProductsContainer from "../../../components/app/Products/ProductsContainer";
import {
  defaultStarterProperties,
  findPropertyMatch,
  getProductsUsingProperty,
  isProductLinkedToProperty,
} from "../../../utils/propertyProducts";
import "./styles.css";
import "../home/styles.css";

const PropertySidebarItemIcon = ({ property, accent }) => {
  const imgUrl = property?.imageUrl || property?.image || property?.images?.[0]?.url;
  const [failedUrl, setFailedUrl] = useState(null);

  const hasFailed = failedUrl === imgUrl;

  if (imgUrl && !hasFailed) {
    return (
      <img
        src={imgUrl}
        alt={property?.label || property?.title || "Property"}
        className="property-sidebar-item-thumb"
        onError={() => setFailedUrl(imgUrl)}
        loading="lazy"
      />
    );
  }

  return (
    <div
      className="property-sidebar-item-icon-fallback"
      style={{ backgroundColor: accent }}
      aria-hidden="true"
    >
      <SpaOutlinedIcon sx={{ fontSize: 16 }} />
    </div>
  );
};

const PropertiesPage = () => {
  const loaderData = useRouteLoaderData("storefront");
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const mobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const allProductsContext = useContext(AllProductContext);
  const allPropertiesContext = useContext(AllPropertiesContext);
  const allReviewsContext = useContext(AllReviewsContext);

  const [rawProducts, setRawProducts] = useState(
    Array.isArray(loaderData?.data) && loaderData.data.length > 0
      ? loaderData.data
      : Array.isArray(allProductsContext?.state) && allProductsContext.state.length > 0
      ? allProductsContext.state
      : []
  );

  const [propertiesList, setPropertiesList] = useState(
    Array.isArray(allPropertiesContext?.state) && allPropertiesContext.state.length > 0
      ? allPropertiesContext.state
      : defaultStarterProperties
  );

  const [isLoading, setIsLoading] = useState(rawProducts.length === 0);
  const [propertySearch, setPropertySearch] = useState("");

  // 1. Fetch reviews for accurate product ratings
  useEffect(() => {
    const reviewsAPI = new ReviewsAPI();
    reviewsAPI
      .getReviews()
      .then((res) => {
        if (res && res.success && allReviewsContext?.setState) {
          allReviewsContext.setState(res.data);
        }
      })
      .catch((err) => {
        console.log("Error loading reviews on properties page:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Fetch products if not supplied via loaderData or context
  useEffect(() => {
    if (Array.isArray(loaderData?.data) && loaderData.data.length > 0) {
      setRawProducts(loaderData.data);
      if (allProductsContext?.setState) {
        allProductsContext.setState(loaderData.data);
      }
      setIsLoading(false);
      return;
    }

    if (Array.isArray(allProductsContext?.state) && allProductsContext.state.length > 0) {
      setRawProducts(allProductsContext.state);
      setIsLoading(false);
      return;
    }

    const productsAPI = new ProductsAPI();
    productsAPI
      .getProducts()
      .then((res) => {
        if (res && res.success && Array.isArray(res.data)) {
          setRawProducts(res.data);
          if (allProductsContext?.setState) {
            allProductsContext.setState(res.data);
          }
        }
      })
      .catch((err) => {
        console.log("Error loading products on properties page:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderData, allProductsContext?.state]);

  // 3. Fetch properties list from Firestore or fallback to default starter properties
  useEffect(() => {
    const propertiesAPI = new PropertiesAPI();
    propertiesAPI
      .getProperties()
      .then((res) => {
        if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
          const activeProps = res.data
            .filter((p) => p.isActive !== false)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((p) => ({
              ...p,
              image: p.imageUrl || p.image,
              link: p.link && p.link !== "/collections" ? p.link : `/properties/${p.id}`,
            }));

          if (activeProps.length > 0) {
            setPropertiesList(activeProps);
            if (allPropertiesContext?.setState) {
              allPropertiesContext.setState(activeProps);
            }
          }
        }
      })
      .catch((err) => {
        console.log("Error loading properties in PropertiesPage:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 4. Resolve current active property
  const currentProperty = useMemo(() => {
    if (propertyId) {
      const matched = findPropertyMatch(propertiesList, propertyId);
      if (matched) return matched;
    }
    // Default to the first available property
    return propertiesList[0] || defaultStarterProperties[0];
  }, [propertyId, propertiesList]);

  // 5. Update browser document title
  useEffect(() => {
    if (currentProperty) {
      const name = currentProperty.label || currentProperty.title || "Ayurvedic Properties";
      document.title = `${name} - Shop by Properties | Inproveda`;
    }
  }, [currentProperty]);

  // 6. Get all products linked to the current property
  const linkedProducts = useMemo(() => {
    if (!currentProperty || !Array.isArray(rawProducts)) return [];
    return getProductsUsingProperty(rawProducts, currentProperty);
  }, [currentProperty, rawProducts]);

  const propertyAccent = currentProperty?.accent || "#047857";
  const propertyLabel = currentProperty?.label || currentProperty?.title || "Property";
  const propertyImage = currentProperty?.imageUrl || currentProperty?.image;
  const propertyDescription =
    currentProperty?.description ||
    `Targeted Ayurvedic herbal formulations specially crafted for ${propertyLabel.toLowerCase()}.`;

  // Breadcrumbs
  const breadcrumbBar = (
    <div className="property-breadcrumb">
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        <Link to="/" style={{ color: "#5b6b68", textDecoration: "none" }}>
          Home
        </Link>
        <Link to="/properties" style={{ color: "#5b6b68", textDecoration: "none" }}>
          Shop By Properties
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
          {propertyLabel}
        </Typography>
      </Breadcrumbs>
    </div>
  );

  // Filter properties by search
  const displayedProperties = useMemo(() => {
    if (!propertySearch.trim()) return propertiesList;
    const q = propertySearch.trim().toLowerCase();
    return propertiesList.filter(
      (p) =>
        (p.label && p.label.toLowerCase().includes(q)) ||
        (p.title && p.title.toLowerCase().includes(q))
    );
  }, [propertiesList, propertySearch]);

  // Left Sidebar: Property Selection Navigation
  const propertySidebar = (
    <div className="property-sidebar-panel">
      <div className="property-sidebar-header">
        <div className="property-sidebar-title-row">
          <div className="property-sidebar-icon-badge" aria-hidden="true">
            <SpaOutlinedIcon sx={{ fontSize: 18 }} />
          </div>
          <h2 className="property-sidebar-title">Shop by Properties</h2>
          <span className="property-sidebar-total-badge">
            {propertiesList.length}
          </span>
        </div>
        <p className="property-sidebar-subtitle">Select a targeted formulation</p>

        <div className="property-sidebar-search">
          <SearchRoundedIcon className="property-sidebar-search-icon" />
          <input
            type="text"
            placeholder="Search properties..."
            value={propertySearch}
            onChange={(e) => setPropertySearch(e.target.value)}
            className="property-sidebar-search-input"
            aria-label="Search properties"
          />
          {propertySearch && (
            <button
              type="button"
              className="property-sidebar-search-clear"
              onClick={() => setPropertySearch("")}
              aria-label="Clear property search"
            >
              <ClearRoundedIcon sx={{ fontSize: 14 }} />
            </button>
          )}
        </div>
      </div>

      <div className="property-sidebar-list" role="tablist" aria-label="Properties selection">
        {displayedProperties.map((item) => {
          const isActive =
            currentProperty &&
            (item.id === currentProperty.id ||
              item.label?.toLowerCase() === currentProperty.label?.toLowerCase());
          const count = rawProducts.filter((prod) => isProductLinkedToProperty(prod, item)).length;
          const accent = item.accent || "#10b981";

          return (
            <button
              key={item.id}
              type="button"
              className={`property-sidebar-item ${isActive ? "is-active" : ""}`}
              onClick={() => navigate(`/properties/${item.id}`)}
              style={
                isActive
                  ? {
                      borderColor: accent,
                      borderLeftWidth: "4px",
                      backgroundColor: `${accent}12`,
                    }
                  : {}
              }
            >
              <PropertySidebarItemIcon property={item} accent={accent} />
              <span className="property-sidebar-item-label">{item.label || item.title}</span>
              <span
                className="property-sidebar-item-count"
                style={
                  isActive
                    ? {
                        backgroundColor: accent,
                        color: "#ffffff",
                      }
                    : {}
                }
              >
                {count}
              </span>
            </button>
          );
        })}

        {displayedProperties.length === 0 && (
          <div className="property-sidebar-empty">
            <p>No properties match "{propertySearch}"</p>
            <button type="button" onClick={() => setPropertySearch("")}>
              Clear search
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Property Switcher for Mobile
  const propertyTabsMobile = (
    <div className="property-tabs-scroll" role="tablist" aria-label="All Properties">
      {propertiesList.map((item) => {
        const isActive =
          currentProperty &&
          (item.id === currentProperty.id ||
            item.label?.toLowerCase() === currentProperty.label?.toLowerCase());
        const count = rawProducts.filter((prod) => isProductLinkedToProperty(prod, item)).length;

        return (
          <button
            key={item.id}
            type="button"
            className={`property-pill-btn ${isActive ? "is-active" : ""}`}
            onClick={() => navigate(`/properties/${item.id}`)}
          >
            <span
              className="property-pill-dot"
              style={{ backgroundColor: item.accent || "#10b981" }}
            />
            <span>{item.label || item.title}</span>
            <span className="property-pill-count">{count}</span>
          </button>
        );
      })}
    </div>
  );

  // Hero Section (Compact)
  const propertyHero = (
    <div
      className="property-hero"
      style={{
        borderLeft: `4px solid ${propertyAccent}`,
      }}
    >
      <div className="property-hero-content">
        <div className="property-hero-text">
          <div className="property-hero-header-row">
            <h1 className="property-hero-title">{propertyLabel}</h1>
            <span
              className="property-count-pill"
              data-testid="property-count-pill"
            >
              <strong>{linkedProducts.length}</strong>
              {linkedProducts.length === 1 ? "Product available" : "Products available"}
            </span>
          </div>

          <p className="property-hero-desc">{propertyDescription}</p>
        </div>

        {propertyImage && (
          <div
            className="property-hero-image-wrap"
            style={{
              borderColor: `${propertyAccent}30`,
            }}
          >
            <img
              src={propertyImage}
              alt={propertyLabel}
              className="property-hero-image"
            />
          </div>
        )}
      </div>
    </div>
  );

  // Product List / Grid
  const renderProductContent = () => {
    // 1. No products linked to this property
    if (!isLoading && linkedProducts.length === 0) {
      return (
        <div className="property-empty-card">
          <div className="property-empty-icon">
            <SpaOutlinedIcon sx={{ fontSize: 36, color: propertyAccent }} />
          </div>
          <h2 className="property-empty-title">No products linked yet</h2>
          <p className="property-empty-text">
            We are curating targeted formulations for <strong>{propertyLabel}</strong>. In the
            meantime, browse our other property ranges or view our full collection of Ayurvedic
            products.
          </p>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              component={Link}
              to="/collections"
              sx={{
                bgcolor: "#064e3b",
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "24px",
                px: 3,
                "&:hover": { bgcolor: "#04392b" },
              }}
            >
              Explore All Collections
            </Button>
            {propertiesList.length > 1 && (
              <Button
                variant="outlined"
                onClick={() => {
                  const other = propertiesList.find((p) => p.id !== currentProperty?.id);
                  if (other) navigate(`/properties/${other.id}`);
                }}
                sx={{
                  borderColor: "#064e3b",
                  color: "#064e3b",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "24px",
                  px: 3,
                }}
              >
                Browse Other Properties
              </Button>
            )}
          </Box>
        </div>
      );
    }

    // 2. Render catalog products grid
    return (
      <ProductsContainer
        heading=""
        headingAlign="left"
        productCount={linkedProducts.length}
        productsArray={linkedProducts}
        showCarousel={false}
        addToCartEnable={true}
        catalogGrid={true}
      />
    );
  };

  // Desktop view
  if (!mobileScreen) {
    return (
      <div className="property-page">
        {breadcrumbBar}
        <div className="property-layout">
          <aside className="property-sidebar">{propertySidebar}</aside>
          <main className="property-main">
            {propertyHero}
            <div className="property-list-wrap">{renderProductContent()}</div>
          </main>
        </div>
      </div>
    );
  }

  // Mobile view
  return (
    <div className="property-page property-page-mobile">
      <div className="property-mobile-chrome">
        <div className="property-mobile-header-row">
          <Link to="/" className="property-mobile-back-btn" aria-label="Back">
            <ArrowBackRoundedIcon sx={{ fontSize: 24 }} />
          </Link>
          <h1 className="property-mobile-title">
            {propertyLabel}
            <span className="property-count-inline">
              ({linkedProducts.length})
            </span>
          </h1>
        </div>

        <div style={{ marginTop: "0.5rem" }}>{propertyTabsMobile}</div>
      </div>

      <div className="property-mobile-list">{renderProductContent()}</div>
    </div>
  );
};

export const DataLoader = async () => {
  const productAPI = new ProductsAPI();
  const resProducts = await productAPI.getProducts();
  return resProducts;
};

export default PropertiesPage;
